import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CancelOutlined as CancelOutlinedIcon,
  ChatBubbleOutlineRounded as ChatBubbleOutlineRoundedIcon,
  PhotoCameraOutlined as PhotoCameraOutlinedIcon,
  FavoriteRounded as FavoriteRoundedIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  StarBorderRounded as StarBorderRoundedIcon,
  Straighten as StraightenIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import type {
  HomeOwnerMatchingProfile,
  HomeOwnerUser,
  UserFromServer,
} from "../../../__generated__/types";
import type { HomeownerProjectRecord } from "../types";
import coverFallback from "../../../assets/our-homeowners-page/Every-Client-Exceptional_d.jpg";
import { VisuallyHiddenInput } from "../../../components/firebase-image-upload";
import { getMatchScoreColors, getMatchScoreValue } from "../../matches-page/utils";
import {
  ACCENT_COLOR,
  AVATAR_OVERLAP,
  AVATAR_SIZE,
  CARD_RADIUS,
  CHIP_COLOR,
  HERO_HEIGHT_MD,
  HERO_HEIGHT_XS,
  MATCHING_RING_CIRCUMFERENCE,
  MATCHING_RING_RADIUS,
  MATCHING_RING_STROKE,
  SECTION_BG,
  SECTION_BORDER,
} from "../constants";
import { getStyleImageCandidates } from "../utils";

const CONTACT_COLOR = "#4F46E5";
const BUTTON_RADIUS = 12;
const BUTTON_HEIGHT = 44;
const BUTTON_FONT_SIZE = "16px";
const COUNTRY_SEGMENTS = new Set([
  "us",
  "usa",
  "u.s",
  "u.s.a",
  "united states",
  "united states of america",
]);

const formatProjectLocationCityState = (propertyLocation?: string | null) => {
  if (!propertyLocation) return null;
  const segments = propertyLocation
    .split(",")
    .map((segment) => segment.trim().replace(/\s+/g, " "))
    .filter(Boolean);
  if (!segments.length) return null;
  const normalizedLastSegment = segments[segments.length - 1]
    .toLowerCase()
    .replace(/\./g, "");
  const filteredSegments = COUNTRY_SEGMENTS.has(normalizedLastSegment)
    ? segments.slice(0, -1)
    : segments;
  if (!filteredSegments.length) return null;
  if (filteredSegments.length === 1) return filteredSegments[0];
  const city = filteredSegments[filteredSegments.length - 2];
  const state = filteredSegments[filteredSegments.length - 1];
  return `${city}, ${state}`;
};

type ZipLookupResult = { city: string; state: string } | null;
const zipLookupCache = new Map<string, ZipLookupResult>();

const normalizeZip = (value?: string | null) => {
  if (!value) return null;
  const match = String(value).trim().match(/(\d{5})/);
  return match ? match[1] : null;
};

const formatCityState = (city?: string | null, state?: string | null) => {
  const parts = [city, state]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

const LeadInfoIcon = ({ color }: { color: string }) => (
  <Box component="span" sx={{ display: "inline-flex", color }}>
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 1.3125C5.87512 1.3125 4.7755 1.64607 3.8402 2.27102C2.90489 2.89597 2.17591 3.78423 1.74544 4.82349C1.31496 5.86274 1.20233 7.00631 1.42179 8.10958C1.64124 9.21284 2.18292 10.2263 2.97833 11.0217C3.77374 11.8171 4.78716 12.3588 5.89043 12.5782C6.99369 12.7977 8.13726 12.685 9.17651 12.2546C10.2158 11.8241 11.104 11.0951 11.729 10.1598C12.3539 9.2245 12.6875 8.12488 12.6875 7C12.6859 5.49207 12.0862 4.04636 11.0199 2.98009C9.95365 1.91382 8.50793 1.31409 7 1.3125ZM7 11.8125C6.04818 11.8125 5.11773 11.5303 4.32632 11.0014C3.53491 10.4726 2.91808 9.72103 2.55383 8.84166C2.18959 7.9623 2.09428 6.99466 2.27997 6.06113C2.46566 5.12759 2.92401 4.27009 3.59705 3.59705C4.27009 2.92401 5.1276 2.46566 6.06113 2.27997C6.99466 2.09428 7.9623 2.18958 8.84167 2.55383C9.72104 2.91808 10.4726 3.53491 11.0014 4.32632C11.5303 5.11773 11.8125 6.04818 11.8125 7C11.8111 8.27591 11.3036 9.49915 10.4014 10.4014C9.49915 11.3036 8.27591 11.8111 7 11.8125ZM7.875 9.625C7.875 9.74103 7.82891 9.85231 7.74686 9.93436C7.66481 10.0164 7.55353 10.0625 7.4375 10.0625C7.20544 10.0625 6.98288 9.97031 6.81878 9.80622C6.65469 9.64212 6.5625 9.41956 6.5625 9.1875V7C6.44647 7 6.33519 6.95391 6.25314 6.87186C6.1711 6.78981 6.125 6.67853 6.125 6.5625C6.125 6.44647 6.1711 6.33519 6.25314 6.25314C6.33519 6.17109 6.44647 6.125 6.5625 6.125C6.79457 6.125 7.01713 6.21719 7.18122 6.38128C7.34531 6.54538 7.4375 6.76794 7.4375 7V9.1875C7.55353 9.1875 7.66481 9.23359 7.74686 9.31564C7.82891 9.39769 7.875 9.50897 7.875 9.625ZM6.125 4.59375C6.125 4.46396 6.16349 4.33708 6.2356 4.22916C6.30771 4.12124 6.4102 4.03712 6.53012 3.98745C6.65003 3.93778 6.78198 3.92479 6.90928 3.95011C7.03658 3.97543 7.15351 4.03793 7.24529 4.12971C7.33707 4.22149 7.39957 4.33842 7.42489 4.46572C7.45021 4.59302 7.43722 4.72497 7.38755 4.84489C7.33788 4.9648 7.25377 5.06729 7.14585 5.1394C7.03793 5.21151 6.91105 5.25 6.78125 5.25C6.6072 5.25 6.44028 5.18086 6.31721 5.05779C6.19414 4.93472 6.125 4.7678 6.125 4.59375Z"
        fill="currentColor"
      />
    </svg>
  </Box>
);

type HeaderSectionProps = {
  effectiveHomeOwnerId: string;
  user: UserFromServer | null;
  homeownerUser: Partial<HomeOwnerUser> | null;
  profile: HomeOwnerMatchingProfile | null;
  projects?: HomeownerProjectRecord[];
  profileImageUrl?: string | null;
  personalityType: string;
  topMatchingAttributes: string[];
  partnershipStyle: string;
  topDesignerSkills: string[];
  leadReadiness: {
    label: string;
    tooltip: string;
    backgroundColor: string;
    textColor: string;
  };
  onContactHomeowner?: () => void;
  isContactDisabled?: boolean;
  contactDisabledReason?: string | null;
  onLove?: () => void;
  onLike?: () => void;
  onDismiss?: () => void;
  showActions?: boolean;
  enableActions?: boolean;
  isLoved?: boolean;
  isLiked?: boolean;
  isDismissed?: boolean;
  canEditProfileImage?: boolean;
  onProfileImageUpload?: (event: ChangeEvent<HTMLInputElement>) => void;
  isProfileImageUploading?: boolean;
  matchScore?: number | null;
  distanceLabel?: string | null;
  matchLocationLabel?: string | null;
  isOnline?: boolean | null;
};

const HeaderSection = ({
  effectiveHomeOwnerId,
  user,
  homeownerUser,
  profile,
  projects = [],
  profileImageUrl,
  personalityType,
  topMatchingAttributes,
  partnershipStyle,
  topDesignerSkills,
  leadReadiness,
  onContactHomeowner,
  isContactDisabled = false,
  contactDisabledReason = null,
  onLove,
  onLike,
  onDismiss,
  showActions = true,
  enableActions = true,
  isLoved = false,
  isLiked = false,
  isDismissed = false,
  canEditProfileImage = false,
  onProfileImageUpload,
  isProfileImageUploading = false,
  matchScore = null,
  distanceLabel = null,
  matchLocationLabel = null,
  isOnline = null,
}: HeaderSectionProps) => {
  const [profileImageError, setProfileImageError] = useState(false);
  const [signupZipLocation, setSignupZipLocation] = useState<ZipLookupResult>(null);
  const isOwnHomeownerProfile =
    user?.accountType?.toLowerCase() === "homeowner" &&
    user?.id === effectiveHomeOwnerId;
  const signupZip = useMemo(
    () => (isOwnHomeownerProfile ? normalizeZip(homeownerUser?.zipCode) : null),
    [homeownerUser?.zipCode, isOwnHomeownerProfile]
  );

  useEffect(() => {
    let cancelled = false;
    if (!signupZip) {
      setSignupZipLocation(null);
      return () => {
        cancelled = true;
      };
    }

    const cached = zipLookupCache.get(signupZip);
    if (cached !== undefined) {
      setSignupZipLocation(cached);
      return () => {
        cancelled = true;
      };
    }

    const lookupZipLocation = async () => {
      try {
        const response = await fetch(
          `https://api.zippopotam.us/us/${encodeURIComponent(signupZip)}`
        );
        if (!response.ok) {
          zipLookupCache.set(signupZip, null);
          if (!cancelled) setSignupZipLocation(null);
          return;
        }
        const data = await response.json();
        const place = Array.isArray(data?.places) ? data.places[0] : null;
        const city =
          typeof place?.["place name"] === "string"
            ? place["place name"].trim()
            : "";
        const state =
          typeof place?.["state abbreviation"] === "string"
            ? place["state abbreviation"].trim()
            : typeof place?.state === "string"
              ? place.state.trim()
              : "";
        const resolved = city || state ? { city, state } : null;
        zipLookupCache.set(signupZip, resolved);
        if (!cancelled) {
          setSignupZipLocation(resolved);
        }
      } catch {
        zipLookupCache.set(signupZip, null);
        if (!cancelled) {
          setSignupZipLocation(null);
        }
      }
    };

    lookupZipLocation();

    return () => {
      cancelled = true;
    };
  }, [signupZip]);

  const displayId = useMemo(() => {
    if (
      user?.accountType?.toLowerCase() === "homeowner" &&
      user?.id === effectiveHomeOwnerId &&
      user?.aliasId
    ) {
      return `Match_${user.aliasId}`;
    }
    const suffix = effectiveHomeOwnerId.slice(-5).toUpperCase();
    return `Match_${suffix || "H12345"}`;
  }, [user?.accountType, user?.id, user?.aliasId, effectiveHomeOwnerId]);

  const locationLabel = useMemo(() => {
    if (typeof matchLocationLabel === "string" && matchLocationLabel.trim()) {
      return matchLocationLabel.trim();
    }

    if (isOwnHomeownerProfile) {
      const fromZip = formatCityState(signupZipLocation?.city, signupZipLocation?.state);
      if (fromZip) return fromZip;
      const fromUser = formatCityState(homeownerUser?.city, homeownerUser?.state);
      if (fromUser) return fromUser;
      return "Location unavailable";
    }

    const firstProjectLocation = formatProjectLocationCityState(
      projects[0]?.propertyLocation
    );
    if (firstProjectLocation) {
      return firstProjectLocation;
    }
    const fromUser = formatCityState(homeownerUser?.city, homeownerUser?.state);
    if (fromUser) return fromUser;

    return "Los Angeles, USA";
  }, [
    isOwnHomeownerProfile,
    projects,
    signupZipLocation?.city,
    signupZipLocation?.state,
    homeownerUser?.city,
    homeownerUser?.state,
    effectiveHomeOwnerId,
    matchLocationLabel,
  ]);

  const initials = useMemo(() => {
    if (
      user?.accountType?.toLowerCase() === "homeowner" &&
      user?.id === effectiveHomeOwnerId
    ) {
      const first = user?.firstName?.[0] ?? "";
      const last = user?.lastName?.[0] ?? "";
      const merged = `${first}${last}`.trim();
      return merged ? merged.toUpperCase() : "Mm";
    }
    return "Mm";
  }, [
    user?.accountType,
    user?.id,
    user?.firstName,
    user?.lastName,
    effectiveHomeOwnerId,
  ]);

  const coverImageCandidates = useMemo(() => {
    const styleValues = (profile?.homeOwnerStyles?.homeOwnerStyle ?? []) as Array<
      string | null | undefined
    >;
    const styleValue = styleValues.find(
      (value): value is string => Boolean(value)
    );
    if (!styleValue) {
      return [coverFallback];
    }
    const primaryStyle = styleValue.includes("+")
      ? styleValue.split("+")[0].trim()
      : styleValue.trim();
    const candidates = getStyleImageCandidates(primaryStyle, 1);
    return [...candidates, coverFallback];
  }, [profile?.homeOwnerStyles?.homeOwnerStyle]);

  const [coverImageIndex, setCoverImageIndex] = useState(0);
  const presenceDotColor =
    isOnline === false ? "#D24B4B" : isOnline === true ? "#0CCE6B" : "#9CA3AF";

  useEffect(() => {
    setCoverImageIndex(0);
  }, [coverImageCandidates]);

  useEffect(() => {
    setProfileImageError(false);
  }, [profileImageUrl]);

  const coverImageSrc = coverImageCandidates[coverImageIndex] ?? coverFallback;
  const hasCoverImage = Boolean(coverImageSrc);
  const hasMatchingScore =
    typeof matchScore === "number" && !Number.isNaN(matchScore);
  const matchingScoreValue = hasMatchingScore
    ? getMatchScoreValue(matchScore)
    : 0;
  const matchingProgress = Math.max(0, Math.min(100, matchingScoreValue));
  const matchingColors = hasMatchingScore
    ? getMatchScoreColors(matchingScoreValue)
    : { start: "#E5E7EB", end: "#E5E7EB" };
  const hasMatchingAttributes = topMatchingAttributes.length > 0;
  const hasPartnershipStyle = Boolean(partnershipStyle && partnershipStyle.trim());
  const hasDesignerSkills = topDesignerSkills.length > 0;

  return (
    <>
      <Box
        sx={{
          height: { xs: HERO_HEIGHT_XS, md: HERO_HEIGHT_MD },
          borderTopLeftRadius: { xs: 0, md: CARD_RADIUS },
          borderTopRightRadius: { xs: 0, md: CARD_RADIUS },
          overflow: "hidden",
          position: "relative",
          backgroundColor: hasCoverImage ? "#111" : "#1F2937",
        }}
      >
        {hasCoverImage ? (
          <Box
            component="img"
            src={coverImageSrc}
            alt="Homeowner style cover"
            onError={() => {
              setCoverImageIndex((prev) =>
                prev + 1 < coverImageCandidates.length ? prev + 1 : prev
              );
            }}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #1F2937 0%, #111827 100%)",
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: { xs: 24, md: 48 },
          top: {
            xs: `calc(${HERO_HEIGHT_XS}px - ${AVATAR_OVERLAP}px)`,
            md: `calc(${HERO_HEIGHT_MD}px - ${AVATAR_OVERLAP}px)`,
          },
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: "50%",
          border: "6px solid #fff",
          boxShadow: "0px 16px 40px rgba(15, 23, 42, 0.16)",
          overflow: "visible",
          backgroundColor: ACCENT_COLOR,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {profileImageUrl && !profileImageError ? (
          <Box
            component="img"
            src={profileImageUrl}
            alt="Homeowner profile"
            onError={() => setProfileImageError(true)}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: "32px",
            }}
          >
            {initials}
          </Typography>
        )}
        {canEditProfileImage && onProfileImageUpload && (
          <IconButton
            component="label"
            disabled={isProfileImageUploading}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#fff",
              border: "1px solid #E4E7EF",
              boxShadow: "0px 4px 10px rgba(15, 23, 42, 0.12)",
              "&:hover": {
                backgroundColor: "#F8FAFC",
              },
            }}
          >
            {isProfileImageUploading ? (
              <CircularProgress size={14} sx={{ color: "#6D63FF" }} />
            ) : (
              <PhotoCameraOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />
            )}
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={onProfileImageUpload}
            />
          </IconButton>
        )}
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            position: "absolute",
            bottom: 6,
            right: 6,
            border: "4px solid #fff",
            backgroundColor: presenceDotColor,
            transform: "translate(10%, 10%)",
            zIndex: 2,
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: "#fff",
          px: { xs: 3, md: 4 },
          pt: {
            xs: `calc(${AVATAR_OVERLAP}px + 24px)`,
            md: `calc(${AVATAR_OVERLAP}px + 24px)`,
          },
          pb: { xs: 3, md: 3 },
          borderBottomLeftRadius: { xs: 0, md: CARD_RADIUS },
          borderBottomRightRadius: { xs: 0, md: CARD_RADIUS },
        }}
      >
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", lg: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ color: ACCENT_COLOR }}
              >
                {displayId}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                <Stack direction="row" spacing={0.6} alignItems="center">
                  <LocationOnOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                  <Typography variant="body2" color="text.secondary">
                    {locationLabel}
                  </Typography>
                </Stack>
                {distanceLabel ? (
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <StraightenIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                    <Typography variant="body2" color="text.secondary">
                      {distanceLabel}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            </Box>
            {showActions && (
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems="stretch"
                justifyContent="flex-end"
                sx={{ width: { xs: "100%", md: "auto" }, display: { xs: "none", md: "flex" } }}
              >
                <Button
                  variant={isLoved ? "contained" : "outlined"}
                  startIcon={<FavoriteRoundedIcon sx={{ fontSize: 18 }} />}
                  onClick={onLove}
                  aria-pressed={isLoved}
                  disabled={!enableActions}
                  sx={{
                    borderRadius: BUTTON_RADIUS,
                    px: 3,
                    minHeight: BUTTON_HEIGHT,
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    lineHeight: "20px",
                    textTransform: "none",
                    boxShadow: "none",
                    backgroundColor: isLoved ? ACCENT_COLOR : "transparent",
                    color: isLoved ? "#fff" : ACCENT_COLOR,
                    borderColor: ACCENT_COLOR,
                    "&:hover": {
                      backgroundColor: ACCENT_COLOR,
                      color: "#fff",
                      boxShadow: "none",
                    },
                  }}
                >
                  Love
                </Button>
                <Button
                  variant={isLiked ? "contained" : "outlined"}
                  startIcon={<StarBorderRoundedIcon sx={{ fontSize: 18 }} />}
                  onClick={onLike}
                  aria-pressed={isLiked}
                  disabled={!enableActions}
                  sx={{
                    borderRadius: BUTTON_RADIUS,
                    px: 3,
                    minHeight: BUTTON_HEIGHT,
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    lineHeight: "20px",
                    textTransform: "none",
                    borderColor: ACCENT_COLOR,
                    color: isLiked ? "#fff" : ACCENT_COLOR,
                    backgroundColor: isLiked ? ACCENT_COLOR : "transparent",
                    "&:hover": {
                      backgroundColor: ACCENT_COLOR,
                      color: "#fff",
                    },
                  }}
                >
                  Like
                </Button>
                <Button
                  variant={isDismissed ? "contained" : "outlined"}
                  startIcon={<CancelOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={onDismiss}
                  aria-pressed={isDismissed}
                  disabled={!enableActions}
                  sx={{
                    borderRadius: BUTTON_RADIUS,
                    px: 3,
                    minHeight: BUTTON_HEIGHT,
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    lineHeight: "20px",
                    textTransform: "none",
                    borderColor: ACCENT_COLOR,
                    color: isDismissed ? "#fff" : ACCENT_COLOR,
                    backgroundColor: isDismissed ? ACCENT_COLOR : "transparent",
                    "&:hover": {
                      backgroundColor: ACCENT_COLOR,
                      color: "#fff",
                    },
                  }}
                >
                  Not for Me
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />}
                  onClick={onContactHomeowner}
                  disabled={isContactDisabled}
                  title={
                    isContactDisabled
                      ? contactDisabledReason || "Messaging is unavailable for this match."
                      : undefined
                  }
                  sx={{
                    borderRadius: BUTTON_RADIUS,
                    px: 3,
                    minHeight: BUTTON_HEIGHT,
                    fontWeight: 600,
                    fontSize: BUTTON_FONT_SIZE,
                    lineHeight: "20px",
                    textTransform: "none",
                    borderColor: CONTACT_COLOR,
                    color: CONTACT_COLOR,
                    "&:hover": { backgroundColor: "rgba(79, 70, 229, 0.08)" },
                  }}
                >
                  Contact Homeowner
                </Button>
              </Stack>
            )}
          </Stack>

          <Box
            sx={{
              backgroundColor: SECTION_BG,
              borderRadius: CARD_RADIUS,
              border: SECTION_BORDER,
              p: { xs: 2, md: 3 },
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <Stack spacing={1.5} alignItems="center">
                  <Box sx={{ position: "relative", width: 120, height: 120 }}>
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <defs>
                        <linearGradient
                          id="homeownerMatchingRingGradient"
                          x1="60"
                          x2="60"
                          y1="0"
                          y2="120"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop offset="0%" stopColor={matchingColors.start} />
                          <stop offset="100%" stopColor={matchingColors.end} />
                        </linearGradient>
                      </defs>
                      <circle
                        cx="60"
                        cy="60"
                        r={MATCHING_RING_RADIUS}
                        fill="none"
                        stroke="#E5E7EB"
                        strokeWidth={MATCHING_RING_STROKE}
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r={MATCHING_RING_RADIUS}
                        fill="none"
                        stroke="url(#homeownerMatchingRingGradient)"
                        strokeWidth={MATCHING_RING_STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${MATCHING_RING_CIRCUMFERENCE} ${MATCHING_RING_CIRCUMFERENCE}`}
                        strokeDashoffset={`${
                          MATCHING_RING_CIRCUMFERENCE -
                          (MATCHING_RING_CIRCUMFERENCE * matchingProgress) / 100
                        }`}
                        transform="rotate(-90 60 60)"
                      />
                    </svg>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 20,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "inset 0px 4px 10px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#1C1C1C",
                          fontFamily: "'Jost', sans-serif",
                          fontWeight: 800,
                          fontSize: "22.98px",
                          lineHeight: 1,
                        }}
                      >
                        {hasMatchingScore ? `${Math.round(matchingScoreValue)}%` : "—"}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(28, 28, 28, 0.6)",
                      maxWidth: 160,
                      fontSize: "12px",
                      lineHeight: "16px",
                    }}
                    align="center"
                  >
                    Smart-Guild Matching
                    <br />
                    Overall Rate
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Personality Type
                    </Typography>
                    <Typography sx={{ fontWeight: 600, color: ACCENT_COLOR }}>
                      {personalityType}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Top Matching Attributes
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.8 }}>
                      {hasMatchingAttributes
                        ? topMatchingAttributes.map((value) => (
                            <Chip
                              key={`match-${value}`}
                              label={value}
                              size="small"
                              sx={{
                                backgroundColor: CHIP_COLOR,
                                color: "#fff",
                                fontWeight: 600,
                                borderRadius: "999px",
                              }}
                            />
                          ))
                        : Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton
                              key={`match-skeleton-${index}`}
                              variant="rounded"
                              width={90 - index * 8}
                              height={28}
                              sx={{ borderRadius: "999px" }}
                            />
                          ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Partnership Style
                    </Typography>
                    {hasPartnershipStyle ? (
                      <Typography sx={{ fontWeight: 600, color: ACCENT_COLOR }}>
                        {partnershipStyle}
                      </Typography>
                    ) : (
                      <Skeleton variant="text" width="70%" height={26} />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Top 3 Designer Skill
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.8 }}>
                      {hasDesignerSkills
                        ? topDesignerSkills.map((value) => (
                            <Chip
                              key={`skill-${value}`}
                              label={value}
                              size="small"
                              sx={{
                                backgroundColor: CHIP_COLOR,
                                color: "#fff",
                                fontWeight: 600,
                                borderRadius: "999px",
                              }}
                            />
                          ))
                        : Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton
                              key={`skill-skeleton-${index}`}
                              variant="rounded"
                              width={96 - index * 10}
                              height={28}
                              sx={{ borderRadius: "999px" }}
                            />
                          ))}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    backgroundColor: "#F3F4F6",
                    borderRadius: 2,
                    p: 2,
                    border: "1px solid #E5E7EB",
                  }}
                >
                    <Stack spacing={1} alignItems="center">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Typography variant="subtitle2" fontWeight={700}>
                          Lead Readiness
                        </Typography>
                      </Stack>
                    <Chip
                      label={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ py: "2px" }}
                        >
                          <span>{leadReadiness.label}</span>
                          <Tooltip title={leadReadiness.tooltip}>
                            <Box sx={{ display: "inline-flex", cursor: "help" }}>
                              <LeadInfoIcon color={leadReadiness.textColor} />
                            </Box>
                          </Tooltip>
                        </Stack>
                      }
                      size="small"
                      sx={{
                        backgroundColor: leadReadiness.backgroundColor,
                        color: leadReadiness.textColor,
                        fontWeight: 600,
                        borderRadius: "999px",
                        width: "fit-content",
                        px: 0.75,
                        "& .MuiChip-label": {
                          px: 1.5,
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    />
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Box>
    </>
  );
};

export default HeaderSection;
